import type { I18nMessageObject } from '@_/types';
import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { z } from 'zod';
import { SharedConfig } from '@_/config/shared-config.ts';
import { i18nObjectSchema } from '@_/form-validators';
import { createFormMutationSubmitHandler, useFormMutation } from '@_/hooks';
import { buttonCommonI18n, labelCommonI18n } from '@_/i18n-common';
import { ErrorMessage, Heading, Text } from '@_/ui';
import { formatLanguageName } from '@_/utils';
import { triggerOnChangeValidator } from '@_/utils-frontend';
import { BaseForm, FormI18nTextInput, FormSubmitButton } from '../../formElements/index.ts';

const formSchema = z.object({
  name: i18nObjectSchema({
    maxLength: SharedConfig.user.nameMaxLength,
    requireOne: true
  })
});

type FormSchema = z.input<typeof formSchema>;

export interface ProfileStepProps {
  email: string;
  onNext: (data: { name: I18nMessageObject }) => void;
}

export function ProfileStep({ email, onNext }: ProfileStepProps) {
  const { locale } = useIntl();

  const form = BaseForm.useAppForm({
    defaultValues: useMemo(
      () =>
        ({
          name: {
            en: email.split('@')[0]
          }
        }) as FormSchema,
      [email]
    ),
    onSubmit: createFormMutationSubmitHandler(),
    validators: {
      onMount: formSchema,
      onBlur: triggerOnChangeValidator,
      onChange: formSchema,
      onSubmit: formSchema
    }
  });

  const { mutation, submit } = useFormMutation({
    form,
    formSchema,
    mutationOptions: {
      mutationFn: async data => {
        return onNext(data);
      }
    }
  });

  return (
    <div>
      <Heading level={2}>
        <FormattedMessage
          id="component:auth.heading.finish-profile"
          defaultMessage="You're almost done!"
        />
      </Heading>

      <form onSubmit={submit}>
        <div className="mt-6 flex flex-col gap-y-4">
          <Text block>
            <FormattedMessage
              id="component:auth.prompt.finish-profile"
              defaultMessage="Just fill in a few more details and you can start using your account."
            />
          </Text>

          {!!mutation.error && <ErrorMessage />}

          <form.AppField
            name="name"
            children={() => (
              <FormI18nTextInput
                autoFocus
                renderLabel={languageCode => (
                  <FormattedMessage
                    {...labelCommonI18n.personsNameInLang}
                    values={{
                      lang: formatLanguageName(locale, languageCode)
                    }}
                  />
                )}
                showAll
              />
            )}
          />

          <div className="mt-4">
            <form.AppForm>
              <FormSubmitButton fillContainer size="lg" type="submit">
                <FormattedMessage {...buttonCommonI18n.continue} />
              </FormSubmitButton>
            </form.AppForm>
          </div>
        </div>
      </form>
    </div>
  );
}
